<script setup lang="ts">
import { whenever } from '@vueuse/core'
import { VSF_MHJ_MY_STORE_SEARCH_TERM } from '~/config/cookie'
import { LocationSubmitType } from '~/types/Store'

type storeSearchProps = {
  stores: SFCCStore[]
  products?: FlattenedProduct[]
  productStoreLocation?: ProductStoreLocation
}
const props = defineProps<storeSearchProps>()
const emits = defineEmits<{
  (e: 'search'): void
}>()
const parentLocation = inject('location', '')
const isCheckoutOrBag = parentLocation === LOCATIONS.checkOut || parentLocation === LOCATIONS.myBag
const isPdp = parentLocation === LOCATIONS.pdp
const isStores = parentLocation === LOCATIONS.stores
const group = isCheckoutOrBag
  ? 'click and collect'
  : isPdp
    ? 'pdp find in store'
    : isStores
      ? 'store locator'
      : 'set my store'
const { loading: storesLoading, fetchStoresByLocation, fetchAllStores } = useStores()
const { loading: storeStockLoading, result: storeStockResult, getStoreStock } = useStoreStock()
const { loading: geoLoading, getGeocoding, result: googleResult } = useGoogleGeoLocation()
const i18n = useI18n()
const locale = i18n.locale.value
const storedSearchTerm = useCookie(VSF_MHJ_MY_STORE_SEARCH_TERM)
const shopStore = useShopStore()
const searchTerm = ref(isCheckoutOrBag ? (storedSearchTerm.value ?? '').toString() : '')
const friendlyError = ref('')
const locationFailure = ref<'denied' | 'unavailable'>()
const locationErrorMessage = computed(() => {
  if (locationFailure.value === 'denied') return i18n.t('set_my_store.location_denied')
  if (locationFailure.value === 'unavailable') return i18n.t('set_my_store.location_error')
  return ''
})
const isButtonClicked = ref(false)
const loading = ref(false)
const isLoading = computed(() => storesLoading.value || geoLoading.value || loading.value || storeStockLoading.value)
const noResultFound = computed(
  () => isButtonClicked.value && !locationFailure.value && props.stores.length === 0 && !isLoading.value,
)
const productStore = props.productStoreLocation ? useProductStore(props.productStoreLocation) : undefined

const useLocationData = async (searchValue: string, submitType: LocationSubmitType) => {
  await getGeocoding(searchValue)
  const latitude = googleResult.value.results?.[0]?.geometry?.location?.lat
  const longitude = googleResult.value.results?.[0]?.geometry?.location?.lng
  if (googleResult.value.results?.[0]?.types.includes('country')) {
    friendlyError.value = i18n.t('product_details_page.find_store.popup.google_error_config')
    handleErrorDataLayer({ errorMessage: friendlyError.value, searchValue, locale, group })
    shopStore.storesList = []
  } else if (longitude && latitude) {
    await getStoresAndStockWithLocation({ longitude, latitude, maxDistance: 150 }, submitType)
  } else {
    friendlyError.value = i18n.t('product_details_page.find_store.popup.google_error_config')
    handleErrorDataLayer({ errorMessage: friendlyError.value, searchValue, locale, group })
    shopStore.storesList = []
  }
  // Fallback: if geocoding failed or returned no nearby stores, search store names in allStoresList
  if (!shopStore.storesList.length) {
    if (!shopStore.allStoresList?.length) {
      shopStore.allStoresList = (await fetchAllStores()) || []
    }
    const storeName = searchValue.toLocaleLowerCase()
    const matchedStore = shopStore.allStoresList.find((s) => s?.name?.toLocaleLowerCase().includes(storeName))
    if (matchedStore) {
      friendlyError.value = ''
      await getStoresAndStockWithLocation(
        { latitude: matchedStore.latitude, longitude: matchedStore.longitude, maxDistance: 150 },
        submitType,
      )
    }
  }
}

const findStore = async (searchValue: string, regex: RegExp, submitType: LocationSubmitType) => {
  if (!searchValue?.match(regex)) {
    return useLocationData(searchValue, submitType)
  }
  getStoresAndStockWithLocation({ postalCode: searchValue, maxDistance: 150 }, submitType)
}

const getStoresAndStockWithLocation = async (
  location: {
    longitude?: number
    latitude?: number
    postalCode?: string
    maxDistance: number
  },
  submitType: LocationSubmitType,
) => {
  const storesResult = await fetchStoresByLocation({ ...location })
  // ensure whenever doing a stock check from search that myStoreId is included specifically for productFindInStore component to be displaying accurate information at all times
  const storeIdsIncludingMyStore = new Set([shopStore.myStoreId, ...storesResult.map((store) => store.id)])
  if (props.products && storesResult.length > 0) {
    await getStoreStock(
      Array.from(storeIdsIncludingMyStore),
      props.products.map(
        (product) =>
          productStore?.selectedProcessedSizeOptions?.specialOrderSKU ||
          productStore?.selectedProcessedSizeOptions?.sfccVariantSKU ||
          product.id ||
          product.productId,
      ),
    )
    if (!storesInStock.value) {
      friendlyError.value = i18n.t('product_details_page.find_store.popup.stock_error_config')
      handleErrorDataLayer({
        errorMessage: friendlyError.value,
        searchValue: searchTerm.value,
        locale,
        group,
      })
    } else {
      handleResultViewDataLayer({
        storeResults: storesResult,
        storeStockResults: storeStockResult.value,
        searchValue: searchTerm.value,
        locale,
        group,
        submitType,
      })
    }
    shopStore.storeStock = storeStockResult.value
  } else if (props.products && storesResult.length === 0) {
    friendlyError.value = i18n.t('product_details_page.find_store.popup.stock_error_config')
    handleErrorDataLayer({
      errorMessage: friendlyError.value,
      searchValue: searchTerm.value,
      locale,
      group,
    })
  }
  shopStore.storesList = storesResult
  shopStore.searchTerm = searchTerm.value
  handleResultViewDataLayer({
    storeResults: shopStore.storesList,
    storeStockResults: storeStockResult.value,
    searchValue: searchTerm.value,
    locale,
    group,
    submitType,
  })
}

// Function to see if at least one store in the radius has stock
const storesInStock = computed(() => {
  const storesInStockCount = storeStockResult.value?.stores?.filter((store) => {
    if (isCheckoutOrBag) {
      // Checks to see if any of the passed in products have stock at the current store
      const storesWithStock =
        store?.stockLevels?.reduce((productsInStock, currentValue) => productsInStock + currentValue.quantity, 0) > 0
      return storesWithStock
    }
    return (store?.stockLevels?.[0]?.quantity || 0) > 0
  })
  return storesInStockCount?.length > 0
})

const successfulStoreStockSearchMessage = computed(() => {
  return i18n.t('product_details_page.find_store.popup.nearest_store_message', {
    storeCode: storedSearchTerm.value,
  })
})

const showStoresNearHeading = computed(
  () => !isCheckoutOrBag && !isStores && props.stores.length > 0 && !!storedSearchTerm.value && !isLoading.value,
)

const storesNearHeading = computed(() =>
  storedSearchTerm.value === 'use my location'
    ? i18n.t('product_details_page.find_store.popup.stores_near_location')
    : i18n.t('product_details_page.find_store.popup.stores_near', { term: storedSearchTerm.value }),
)

let latestSearchId = 0
// The permission prompt can outlive this instance; a late answer must not touch the shared store.
const cancelPendingSearch = () => {
  latestSearchId++
  loading.value = false
}
onBeforeUnmount(cancelPendingSearch)
// Set My Store hides this with v-show instead of unmounting it, so closing the modal must cancel too
if (group === 'set my store') whenever(() => !useUIStore().isShowSetMyStoreModal, cancelPendingSearch)

const handleSearch = async (type: string, action: LocationSubmitType, trackSubmit = true) => {
  // A location lookup can wait on the permission prompt indefinitely; a newer search must win.
  const searchId = ++latestSearchId
  const isSuperseded = () => searchId !== latestSearchId
  friendlyError.value = ''
  locationFailure.value = undefined
  loading.value = true
  try {
    if (type === 'near stores') {
      storedSearchTerm.value = action
      handleSubmitDataLayer({ submitType: action, searchValue: type, locale, group })
      const located = await getCurrentGeoPosition()
      if (isSuperseded()) return
      if ('failure' in located) {
        locationFailure.value = located.failure
        shopStore.storesList = []
        handleErrorDataLayer({ errorMessage: locationErrorMessage.value, searchValue: action, locale, group })
        return
      }
      isButtonClicked.value = true
      const storesResult = await fetchStoresByLocation({
        latitude: located.position.coords.latitude,
        longitude: located.position.coords.longitude,
        maxDistance: 150,
      })
      // ensure whenever doing a stock check from search that myStoreId is included specifically for productFindInStore component to be displaying accurate information at all times
      const storeIdsIncludingMyStore = new Set([shopStore.myStoreId, ...storesResult.map((store) => store.id)])
      if (props.products && storesResult.length > 0) {
        await getStoreStock(
          Array.from(storeIdsIncludingMyStore),
          props.products.map(
            (product) =>
              productStore?.selectedProcessedSizeOptions?.specialOrderSKU ||
              productStore?.selectedProcessedSizeOptions?.sfccVariantSKU ||
              product.id ||
              product.productId,
          ),
        )
        if (!storesInStock.value) {
          friendlyError.value = i18n.t('product_details_page.find_store.popup.stock_error_config')
          handleErrorDataLayer({
            errorMessage: friendlyError.value,
            searchValue: searchTerm.value,
            locale,
            group,
          })
        }
        shopStore.storeStock = storeStockResult.value
      }
      shopStore.storesList = storesResult
      handleResultViewDataLayer({
        storeResults: storesResult,
        storeStockResults: storeStockResult.value,
        searchValue: action,
        submitType: action,
        locale,
        group,
      })
    }
    if (type === 'find store') {
      isButtonClicked.value = true
      storedSearchTerm.value = searchTerm.value
      if (trackSubmit) handleSubmitDataLayer({ submitType: action, searchValue: searchTerm.value, locale, group })
      if (!searchTerm.value) {
        shopStore.storesList = []
        friendlyError.value = i18n.t('product_details_page.find_store.popup.google_error_config')
        handleErrorDataLayer({
          errorMessage: friendlyError.value,
          searchValue: searchTerm.value,
          locale,
          group,
        })
        return
      }
      if (locale === 'en-AU') {
        await findStore(searchTerm.value, REGEX_POSTCODE_AU, action)
      }
      if (locale === 'en-NZ') {
        await findStore(searchTerm.value, REGEX_POSTCODE_NZ, action)
      }
      if (locale === 'en-CA' || locale === 'fr') {
        await findStore(searchTerm.value, REGEX_POSTCODE_CA, action)
      }
    }
  } catch (error) {
    shopStore.storesList = []
    shopStore.storeStock = { stores: [] }
  } finally {
    if (!isSuperseded()) {
      loading.value = false
      emits('search')
    }
  }
}

watch(
  () => useUIStore().isSetMyStoreAutoLocate,
  async (isAutoLocate) => {
    if (!isAutoLocate || group !== 'set my store') return
    useUIStore().isSetMyStoreAutoLocate = false
    const storeBefore = shopStore.myStoreId
    const idBeforePermission = latestSearchId
    const permission = await getGeolocationPermission()
    if (idBeforePermission !== latestSearchId) return
    const search = handleSearch('near stores', 'use my location')
    const searchId = latestSearchId
    const isSuperseded = () => searchId !== latestSearchId
    await search
    if (isSuperseded()) return
    let response: LocationResponse = 'accepted'
    if (locationFailure.value === 'denied') response = permission === 'denied' ? 'blocked' : 'declined'
    else if (locationFailure.value === 'unavailable') response = 'unavailable'
    handleLocationResponseDataLayer({ response, locale, group })

    await nextTick()
    const nearestStore = props.stores[0]
    // A store the shopper picked while the prompt was open wins over the nearest one
    const canAutoSelect = () => !isSuperseded() && shopStore.myStoreId === storeBefore
    // Set My Store opens only to explain a location message, no nearby store or a failed save
    const showSetMyStore = () => {
      if (canAutoSelect()) useUIStore().isShowSetMyStoreModal = true
    }
    if (!nearestStore) return showSetMyStore()
    if (!canAutoSelect()) return
    // A close, reopen or newer pick during a slow account save wins
    const isStillLatestPick = () => !isSuperseded() && shopStore.myStore?.id === nearestStore.id
    // false means a signed-in save failed and the account's store was put back
    if (!(await shopStore.setMyStore(nearestStore))) return showSetMyStore()
    if (!isStillLatestPick()) return
    handleResultClickDataLayer({
      storeResults: props.stores,
      storeStockResults: storeStockResult.value,
      searchValue: storedSearchTerm.value?.toString() ?? '',
      storeIndex: 0,
      linkDetails: { label: 'set my store' },
      locale,
      group,
    })
  },
  { immediate: true },
)
// Runs a search the shopper didn't submit (change / clear store), so it skips the submit event.
defineExpose({
  search: (term: string) => {
    searchTerm.value = term
    return handleSearch('find store', 'button click', false)
  },
})
</script>

<template>
  <div class="store-search">
    <LoadingSpinner v-if="isLoading" />
    <span v-if="!isStores" class="store-search__cta">
      {{ $t('product_details_page.find_store.popup.cta_label') }}
    </span>
    <div class="store-search__input-wrapper">
      <Input
        v-model="searchTerm"
        :id="`store-search-input-${parentLocation}`"
        class="store-search__input"
        type="text"
        name="storeSearch"
        :label="$t('product_details_page.find_store.popup.cta_placeholder')"
        :error="friendlyError"
        input-test-id="search-stores__input"
        @keydown.enter.stop.prevent="handleSearch('find store', 'enter press')"
      />
      <Button
        class="store-search__find-button"
        variant="primary"
        appearance="Button"
        @click="handleSearch('find store', 'button click')"
      >
        {{ $t('product_details_page.find_store.popup.button_find') }}
      </Button>
    </div>
    <span v-if="isCheckoutOrBag && stores.length > 0 && products && isButtonClicked && storesInStock">
      {{ successfulStoreStockSearchMessage }}</span
    >
    <Button
      v-show="!searchTerm || isStores"
      variant="text"
      class="store-search__current-location"
      data-testid="search-stores__current-location"
      @click="handleSearch('near stores', 'use my location')"
    >
      <svg-icon name="mhj:location-target" class="store-search__current-location__icon" />
      <span>{{ $t('set_my_store.current_location') }}</span>
    </Button>
    <p v-if="showStoresNearHeading" class="store-search__results-heading">
      {{ storesNearHeading }}
    </p>
    <p
      v-if="locationErrorMessage"
      class="store-search__location-error"
      data-testid="search-stores__location-error"
      role="alert"
    >
      {{ locationErrorMessage }}
    </p>
    <StoreNoSearchResults v-else-if="noResultFound" data-testid="search-stores__no-results" />
  </div>
</template>

<style lang="postcss">
.store-search {
  display: flex;
  flex-direction: column;
  gap: var(--space-50);

  .store-search__input-wrapper {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
  }

  .store-search__input {
    flex-grow: 1;
  }

  .store-search__find-button {
    min-height: 3.125rem;
    border-color: black;
    width: fit-content;
    min-width: fit-content;
    padding: 0 var(--space-80);
  }

  .store-search__current-location {
    display: flex;
    gap: var(--space-20);
    .store-search__current-location__icon {
      width: var(--space-50);
      height: var(--space-50);
    }
  }

  .store-search__results-heading {
    margin: 0;
    color: var(--text-secondary);
  }

  .store-search__location-error {
    margin: var(--space-50) 0 0;
    padding: 1rem;
    text-align: center;
  }
}
</style>
